import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  useClearNotificationsMutation,
  useLazyGetNotificationsDMQuery,
  useLazyGetNotificationsQuery,
  useReadNotificationsMutation,
} from '@/store/api/notificationApi';
import { useLazyGetPreferencesQuery } from '@/store/api/userApi';
import { useAuth } from '@/providers/AuthProvider';
import { setNotificationFeatureFlag, setUnreadNotificationsCount } from '@/store/slices/applicationSlice';
import { NotificationAlert } from '@/data/notifications';

export default function useNotifications(isVisible: boolean) {
  const { user } = useAuth();
  const dispatch = useDispatch();

  const [fetchUserPreferences, { data: userPreferences, isLoading: isLoadingUserPreferences }] =
    useLazyGetPreferencesQuery();
  const [fetchNotifications, { data: notifications, isLoading: isLoadingNotifications }] = useLazyGetNotificationsQuery(
    {
      pollingInterval: 60000,
    }
  );
  const [fetchDeliveryMethods, { data: deliveryMethods, isLoading: isLoadingDeliveryMethods }] =
    useLazyGetNotificationsDMQuery();

  const [readNotifications] = useReadNotificationsMutation();
  const [clearNotifications, { isLoading: isClearingNotifications }] = useClearNotificationsMutation();

  useEffect(() => {
    fetchUserPreferences({});
  }, []);

  useEffect(() => {
    if (userPreferences) {
      dispatch(setNotificationFeatureFlag(userPreferences.notifications_enabled));
    }
  }, [userPreferences]);

  useEffect(() => {
    if (userPreferences?.notifications_enabled) {
      fetchData();
    }
  }, [userPreferences]);

  useEffect(() => {
    if (isVisible) {
      fetchData();
    }
  }, [isVisible]);

  const isLoading = useMemo(() => {
    return isLoadingUserPreferences || isLoadingNotifications || isLoadingDeliveryMethods;
  }, [isLoadingUserPreferences, isLoadingNotifications, isLoadingDeliveryMethods]);

  const enabledNotifications = useMemo(() => {
    if (deliveryMethods && notifications) {
      const enabledEventTypes = deliveryMethods
        .filter((item) => {
          const isDashboardEnabled = item?.delivery_methods.filter((deliveryMethod) => {
            return deliveryMethod.name.toLowerCase() === 'dashboard' && deliveryMethod.enabled;
          });

          return isDashboardEnabled?.length;
        })
        .map((item) => item?.event_type);

      const enabledNotificationsArray = notifications.filter((item: NotificationAlert) =>
        enabledEventTypes.includes(item.event_type)
      );
      return enabledNotificationsArray;
    } else {
      return [];
    }
  }, [notifications, deliveryMethods]);

  const notificationsIds = useMemo(
    () => enabledNotifications.map((item: NotificationAlert) => item.notification_id),
    [enabledNotifications]
  );

  useEffect(() => {
    if (enabledNotifications.length) {
      const unreadNotifications = enabledNotifications.filter((item: NotificationAlert) => !item.read);
      dispatch(setUnreadNotificationsCount(unreadNotifications.length));
    }
  }, [enabledNotifications]);

  const fetchData = () => {
    fetchDeliveryMethods({ uids: user?.uid });
    fetchNotifications({});
  };

  const markAsRead = async () => {
    if (notificationsIds.length && isVisible) {
      await readNotifications([...notificationsIds]);
      await fetchNotifications({});
    }
  };

  const clearAllNotifications = async () => {
    await clearNotifications([...notificationsIds]);
    await fetchNotifications({});
  };

  useEffect(() => {
    markAsRead();
  }, [isVisible, notificationsIds]);

  return {
    isLoading,
    isClearingNotifications,
    clearAllNotifications,
    enabledNotifications,
  };
}
